import axios, { GenericAbortSignal } from 'axios';
import { routes } from './routes';
import { mapClientData } from '../shared/ptCC';
import { CheckoutManifest, startCheckout } from "@easypaypt/checkout-sdk";
import {
    LoggedUser,
    UserData,
    UserPreferencesData,
    UpdateUserData,
    CreateUserData,
    ClientData,
    ProfessionalData,
    AppointmentData,
    SettingsData,
    Notification,
    IncomingMessageNotification,
    IncomingMessagesSummary,
    GeneralSettingsData,
    ChangeLog,
    ReportsSettingsData,
    ReportTemplateData,
    CatalogCategoryData,
    ReportSettings,
    GenerateReportTemplateRequest,
    Report,
    RemindersSettingsData,
    PackSettings,
    ClientPack,
    ClientOrder,
    ClientReportsAndFiles,
    ClientFile,
    TestCommunication,
    AdminStats,
    CommunicationQuotas,
    CommunicationData,
    PaymentData,
    PaymentOrigin,
    PaymentStatus,
    ExpenseData,
    StockData,
    StockHistoryData,
    ChangeEntityLog,
    PaymentsSettingsData,
    ExtrasSettingsData,
    CompanyPublicData,
    ResetPasswordData,
    ClientsLoginSettingsData,
    SelfScheduleRequest,
    Availability,
    CampaignReduced,
    Campaign,
    CampaignBase,
    CampaignTest,
    CampaignAnalytics,
    CampaignOrder,
    WhatsAppTemplateCatalogItem,
    CreateWhatsAppTemplateRequest,
    UpdateWhatsAppTemplateRequest,
    WhatsAppMessageTemplate,
    ClientSearch,
    CampaignRecipientEstimate,
    IntegrationsSettingsData,
    ConsentManagementSettingsData,
    Consents,
    Invoice,
    InvoiceData,
    InvoiceManyAppointmentsData,
    InvoiceReduced,
    InvoiceSummaryData,
    InvoiceDocumentType,
    InvoiceState,
    ChangeInvoiceState,
    BulkAppointmentCreationResult,
    AlertsSettingsData,
    CampaignProcess,
    DialogData,
    AnonymousScheduleRequest,
    IntegrationSyncRequest,
    IntegrationSyncResult,
    IntegrationType,
    InvoiceSettingsData,
    InvoiceCatalogData,
    UpdateStockQuantity,
    TaxExemption,
    TaxRate,
    CreditNoteData,
    MarkInvoicePaidData,
    InvoiceEmailSending,
    SubscriptionDataResponse,
    SubscriptionData,
    CommunicationType,
    AppointmentChangeOrigin,
    AppointmentChangeReason,
    AppointmentUpdate,
    AppointmentChangeRecordData,
    CashRegisterSettings,
    WaitingListSettingsData,
    PrescriptionsSettingsData,
    MedicineSearchResultData,
    CashRegisterEntryData,
    MfaRequiredResponse,
    MfaMethodEnum,
    WaitingListItem,
    WaitingListHistoryData,
    TaskItemData,
    TaskItemStatus,
    CreateTaskItemData,
    UpdateTaskItemStatusData,
    UpdateTaskItemData,
    AssignTaskItemData,
    TaskManagementSettingsData,
    MailerLiteApiKeyRequest,
    MailerLiteFieldsResponse,
    ClientCommunicationsResponse,
    GoogleReviewAnalyticsData,
    PrescriptionData,
    ShareOptions,
    AssignInvoiceData,
} from '../models';
import { GenerateSurveyRequest, SendSurveyToContactRequest, SurveyAnalyticsData, SurveyDraftData, SurveySettingsData, SurveyTokenData, SurveyUpdateRequest } from '../shared/surveys';
import { tryDateString } from '../shared/utils';
import { ApplicationState } from '../store';
import { isConflictError } from './errors';
import { translate } from '../store/translations/hooks';
import { TK } from '../store/translations/translationKeys';
import { getMyUser } from '../store/session/selectors';

function getParams(params: {[name: string]: any}) {
    const paramsString = Object.keys(params)
        .filter(k => !!params[k])
        .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k].toString()))
        .join('&');
        
    return paramsString.length === 0 ? '' : '?'.concat(paramsString);
 }

function getNullableParams(params: {[name: string]: any}) {
    const paramsString = Object.keys(params)
        .filter(k => params[k] !== undefined && params[k] !== null && params[k] !== '')
        .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k].toString()))
        .join('&');

    return paramsString.length === 0 ? '' : '?'.concat(paramsString);
}

export interface GetTasksParams {
    status?: TaskItemStatus;
    assignedUserId?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
}

export interface TasksPendingSummary {
    count: number;
    tasks: TaskItemData[];
}

export const authenticateAsync = (username: string, password: string, isClient: boolean, companyId?: string, impersonateUsername?: string): Promise<LoggedUser | MfaRequiredResponse> => {
    const route = isClient ? routes.clientAuthenticate() : routes.userAuthenticate();
    return axios.post(route, {
        username,
        password,
        isClient,
        companyId: companyId ? parseInt(companyId) : undefined,
        impersonateUsername,
    });
}

export const verifyMfaAsync = (sessionId: string, otp: string, mfaMethod?: MfaMethodEnum): Promise<LoggedUser> => {
    return axios.post(routes.userVerifyMfa(), { sessionId, otp, mfaMethod: mfaMethod ?? MfaMethodEnum.Sms });
}

export const getAuthenticatorSetupAsync = (): Promise<{ secret: string; otpAuthUri: string; sessionId: string }> => {
    return axios.get(routes.usersMeAuthenticatorSetup());
}

export const confirmAuthenticatorSetupAsync = (sessionId: string, code: string): Promise<{ success: boolean }> => {
    return axios.post(routes.usersMeAuthenticatorConfirm(), { sessionId, code });
}

export const disableAuthenticatorAsync = (): Promise<{ success: boolean }> => {
    return axios.delete(routes.usersMeAuthenticatorDisable());
}

export const authenticateWithGoogleAsync = (idToken: string, isClient?: boolean, companyId?: number): Promise<LoggedUser> => {
    const route = isClient ? routes.clientAuthenticate() : routes.userAuthenticate();
    return axios.post(route, {
        googleAuth: {
            idToken,
            isClient,
            companyId,
        },
        isClient,
    });
}

export interface SignUpServiceSetup {
    name: string;
    defaultPrice?: number;
}

export interface SignUpProfessionalSetup {
    name?: string;
    email?: string;
}

export interface SignUpRequest {
    company?: string;
    username?: string;
    password?: string;
    referralCode?: string;
    googleAuth?: {
        idToken: string;
        companyId?: number;
        isClient?: boolean;
    };
    servicesWithPrice?: SignUpServiceSetup[];
    rooms?: string[];
    professionals?: SignUpProfessionalSetup[];
    services?: string[];
    professionalEmails?: string[];
    notifyProfessionalsByEmail?: boolean;
    acceptedTermsAndConditions?: boolean;
    acceptedPrivacyPolicy?: boolean;
    marketingAcceptedByEmail?: boolean;
    consentCulture?: string;
    countryCode?: string;
    partnerCode?: string;
    partnerId?: string;
}

export const signUpAsync = (request: SignUpRequest): Promise<LoggedUser> => {
    return axios.post(routes.userSignUp(), request);
}

export const logoutAsync = (isClient: boolean): Promise<void> => {
    return axios.post(isClient ? routes.clientLogout() : routes.userLogout());
}

export const getCCData = (): Promise<Omit<ClientData,'id'>|undefined> => {
    return fetch("http://localhost:44357/api/cc", {mode: 'cors'})
        .then(response => response.ok ? response.json() : Promise.resolve(undefined))
        .then(mapClientData);
}

export const getUsersAsync = (keyword?: string, pageSize?: number, offset?: number): Promise<UserData[]> => {
    return axios.get(routes.users()+getParams({keyword, pageSize, offset}));
}

export const getUserChangeLogAsync = (id: string, pageSize?: number, offset?: number): Promise<ChangeLog[]> => {
    return axios.get(routes.userChangeLog(id)+getParams({pageSize, offset}));
}

export const deleteUserAsync = (id: string): Promise<void> => {
    return axios.delete(routes.user(id));
}

export const updateUserAsync = (id: string, data: UpdateUserData): Promise<void> => {
    return axios.put(routes.user(id), data);
}

export const createUserAsync = (data: CreateUserData): Promise<string> => {
    return axios.post(routes.users(), data);
}

export const updateMeAsync = (data: UpdateUserData): Promise<void> => {
    return axios.put(routes.usersMe(), data);
}

export const updateMePreferencesAsync = (data: UserPreferencesData): Promise<UserPreferencesData> => {
    return axios.put(routes.usersMePreferences(), data).then(response => response.data);
}

export const uploadUserProfilePictureAsync = (id: string, file: File): Promise<{ url: string; photoFileId: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.patch(routes.userProfilePic(id), formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
}

export const uploadProfessionalProfilePictureAsync = (id: string, file: File): Promise<{ url: string; photoFileId: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.patch(routes.professionalProfilePic(id), formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
}

export const uploadMyProfessionalProfilePictureAsync = (file: File): Promise<{ url: string; photoFileId: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.patch(routes.professionalMeProfilePic(), formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
}

export const removeUserProfilePictureAsync = (id: string): Promise<void> => {
    return axios.patch(routes.userProfilePic(id), null);
}

export const removeProfessionalProfilePictureAsync = (id: string): Promise<void> => {
    return axios.patch(routes.professionalProfilePic(id), null);
}

export const removeMyProfessionalProfilePictureAsync = (): Promise<void> => {
    return axios.patch(routes.professionalMeProfilePic(), null);
}

export const getClientsAsync = (keyword?: string, pageSize?: number, offset?: number): Promise<ClientData[]> => {
    return axios.get(routes.clients()+getParams({keyword, pageSize, offset}));
}

export const getClientsByIdAsync = (ids: string[]): Promise<ClientData[]> => {
    return axios.get(routes.clients()+getParams({ids}));
}

export const getClientAsync = (id: string): Promise<ClientData> => {
    return axios.get(routes.client(id));
}

export const getClientsSearchAsync = (search: ClientSearch): Promise<ClientData[]> => {
    return axios.get(routes.clientsSearch()+getParams(search));
}

export const getClientDuplicatesAsync = (field: string): Promise<{ fieldValue: string; clients: ClientData[] }[]> => {
    return axios.get(routes.clientsDuplicates() + getParams({ field }));
}

export const getClientEntityCountsAsync = (clientIds: string[]): Promise<{ clientId: string; appointments: number; packs: number; orders: number; total: number }[]> => {
    return axios.post(routes.clientsEntityCounts(), clientIds);
}


export const getClientAppointmentsAsync = (id: string, from?: Date, to?: Date, pageSize?: number, offset?: number): Promise<AppointmentData[]> => {
    return axios.get(routes.clientAppointments(id)+getParams({from: tryDateString(from), to: tryDateString(to), pageSize, offset}));
}

export const getClientAppointmentsChangesAsync = (id: string, pageSize?: number, offset?: number): Promise<AppointmentChangeRecordData[]> => {
    return axios.get(routes.clientAppointmentsChanges(id)+getParams({pageSize, offset}));
}

export const getClientsReducedDataAsync = (): Promise<ClientData[]> => {
    return axios.get(routes.clientsReduced());
}

export const getClientChangeLogAsync = (id: string, pageSize?: number, offset?: number): Promise<ChangeLog[]> => {
    return axios.get(routes.clientChangeLog(id)+getParams({pageSize, offset}));
}

export const getClientReportsAsync = (id: string, pageSize?: number, offset?: number): Promise<ClientReportsAndFiles> => {
    return axios.get(routes.clientReports(id)+getParams({pageSize, offset}));
}

export const setClientMintuPauseAsync = (clientId: string, pauseHours?: number): Promise<{ mintuPausedUntil?: string }> => {
    return axios.patch(routes.clientMintuPause(clientId), pauseHours != null ? { pauseHours } : {});
}

export const getClientCommunicationsAsync = (id: string, limit?: number, offset?: number): Promise<ClientCommunicationsResponse> => {
    return axios.get(routes.clientCommunications(id)+getParams({limit, offset}));
}

export const sendClientCommunicationAsync = (
    id: string,
    channel: CommunicationType,
    message?: string,
    subject?: string,
    attachments?: File[],
    conversationId?: string,
    observations?: string,
): Promise<CommunicationData> => {
    const formData = new FormData();
    formData.append('channel', channel.toString());
    if (message != null) {
        formData.append('message', message);
    }
    if (subject) {
        formData.append('subject', subject);
    }
    if (conversationId) {
        formData.append('conversationId', conversationId);
    }
    if (observations != null) {
        formData.append('observations', observations);
    }

    attachments?.forEach(file => formData.append('attachments', file));

    return axios.post(routes.sendClientCommunication(id), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
    });
}

export const requestClientGoogleReviewAsync = (
    clientId: string,
    channel: CommunicationType,
    branchId?: string,
): Promise<CommunicationData> =>
    axios.post(routes.requestClientGoogleReview(clientId), { channel, branchId });

export const updateClientCommunicationObservationsAsync = (
    clientId: string,
    communicationId: string,
    observations?: string,
): Promise<CommunicationData> => {
    return axios.patch(
        routes.updateClientCommunicationObservations(clientId, communicationId),
        { observations: observations ?? null },
    );
}

export const getClientPacksAsync = (id: string, pageSize?: number, offset?: number): Promise<ClientPack[]> => {
    return axios.get(routes.clientPacks(id)+getParams({pageSize, offset}));
}

export const getClientOrdersAsync = (clientId: string, pageSize?: number, offset?: number): Promise<ClientOrder[]> => {
    return axios.get(routes.clientOrders(clientId)+getParams({pageSize, offset}));
}

export const getClientOrderByIdAsync = (orderId: string): Promise<ClientOrder> => {
    return axios.get(routes.orderById(orderId));
}

export const getOrderChangeLogAsync = (clientId: string, orderId: string, pageSize?: number, offset?: number): Promise<ChangeLog[]> => {
    return axios.get(routes.clientOrderChangeLog(clientId, orderId) + getParams({ pageSize, offset }));
}

export const createClientOrderAsync = (order: ClientOrder): Promise<ClientOrder> => {
    return axios.post(routes.clientOrders(order.clientId), order);
}

export const updateClientOrderAsync = (order: ClientOrder): Promise<ClientOrder> => {
    return axios.put(routes.clientOrder(order.clientId, order.id), order);
}

export const getClientPackAsync = (_clientId: string, packId: string): Promise<ClientPack> => {
    return axios.get(routes.pack(packId));
}

export const getPackAsync = (packId: string): Promise<ClientPack> => {
    return axios.get(routes.pack(packId));
}

export const postClientResetPasswordAsync = (id: string): Promise<void> => {
    return axios.post(routes.clientResetPassword(id));
}

export type ConsentDeliveryChannel = 'Email' | 'WhatsApp' | 'SMS';

const consentDeliveryChannelValue: Record<ConsentDeliveryChannel, number> = { Email: 0, WhatsApp: 1, SMS: 2 };

export const postSendConsentApprovalAsync = (id: string, channel: ConsentDeliveryChannel): Promise<{ channel: ConsentDeliveryChannel; sentAt: string }> => {
    return axios.post(routes.clientSendConsentApproval(id), { channel: consentDeliveryChannelValue[channel] });
}

export const getConsentDataAsync = (token: string): Promise<ConsentManagementSettingsData> => {
    return axios.get(routes.consentVerify(), { headers: { Authorization: `Bearer ${token}` } });
}

export const postApproveConsentsAsync = (token: string, approvedConsents: Consents, approvedCustomCheckboxTexts: string[] = []): Promise<void> => {
    return axios.post(routes.consentApprove(), { approvedConsents, approvedCustomCheckboxTexts }, { headers: { Authorization: `Bearer ${token}` } });
}

export const downloadConsentPdfAsync = (): Promise<Blob> => {
    return axios.get(routes.settingsConsentManagementPdf(), { responseType: 'blob' }) as unknown as Promise<Blob>;
}

export const uploadPhysicalConsentAsync = (clientId: string, file: File, approvedConsents: Consents, approvedCustomCheckboxTexts: string[] = []): Promise<ClientFile> => {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(approvedConsents).forEach(([key, value]) => {
        formData.append(`approvedConsents.${key}`, String(value));
    });
    approvedCustomCheckboxTexts.forEach((text, i) => {
        formData.append(`approvedCustomCheckboxTexts[${i}]`, text);
    });
    return axios.post(routes.clientPhysicalConsent(clientId), formData).then(r => r.data);
}

export const confirmEmailWithTokenAsync = (token: string): Promise<void> => {
    return axios.get(routes.usersConfirmEmailPage(), { headers: { Authorization: `Bearer ${token}` } });
}

export const resetPasswordWithTokenAsync = (token: string): Promise<void> => {
    return axios.get(routes.usersResetPasswordPage(), { headers: { Authorization: `Bearer ${token}` } });
}

export const deleteClientAsync = (id: string, migrateToClientId?: string): Promise<void> => {
    return axios.delete(routes.client(id), { params: migrateToClientId ? { migrateToClientId } : undefined });
}

export const deleteClientFileAsync = (id: string, fileId: string): Promise<void> => {
    return axios.delete(routes.clientFile(id, fileId));
}

export const updateClientFileShareAsync = (clientId: string, fileId: string, share: ShareOptions): Promise<void> => {
    return axios.patch(`${routes.clientFile(clientId, fileId)}/share`, share);
}

export const updateClientReportShareAsync = (clientId: string, reportId: string, share: ShareOptions): Promise<void> => {
    return axios.patch(`${routes.clientReport(clientId, reportId)}/share`, share);
}

export const sendClientFileByEmailAsync = (id: string, fileId: string): Promise<void> => {
    return axios.post(routes.clientFileSendEmail(id, fileId));
}

export const deleteClientReportAsync = (id: string, reportId: string): Promise<void> => {
    return axios.delete(routes.clientReport(id, reportId));
}

export const sendClientReportByEmailAsync = (id: string, reportId: string, reportPdf?: File): Promise<void> => {
    if (!reportPdf) {
        return axios.post(routes.clientReportSendEmail(id, reportId));
    }

    const formData = new FormData();
    formData.append('reportPdf', reportPdf);

    return axios.post(routes.clientReportSendEmail(id, reportId), formData);
}

export const updateClientAsync = (id: string, data: ClientData): Promise<void> => {
    return axios.put(routes.client(id), data);
}

export const createClientAsync = (data: ClientData): Promise<{ id: string; clientFileNumber?: number }> => {
    return axios.post(routes.clients(), data);
}

export const updateClientInternalNotesAsync = (id: string, internalNotes: string): Promise<void> => {
    return axios.put(routes.clientInternalNotes(id), {internalNotes});
}

export const updateClientTagsAsync = (id: string, data: string[]): Promise<void> => {
    return axios.put(routes.clientTags(id), data);
}

export const createClientReportAsync = (clientId: string, report: Report): Promise<string> => {
    return axios.post(routes.clientReports(clientId), report);
}

export const updateClientReportAsync = (clientId: string, report: Report): Promise<void> => {
    return axios.put(routes.clientReport(clientId, report.id), report);
}

export const createClientFileAsync = (clientId: string, formData: FormData): Promise<ClientFile> => {
    return axios.post(routes.clientFiles(clientId), formData);
}

export const getProfessionalAsync = (id: string): Promise<ProfessionalData> => {
    return axios.get(routes.professional(id));
}

export const getProfessionalsAsync = (keyword?: string, pageSize?: number, offset?: number): Promise<ProfessionalData[]> => {
    return axios.get(routes.professionals()+getParams({keyword, pageSize, offset}));
}

export const getProfessionalsByIdAsync = (ids: string[]): Promise<ProfessionalData[]> => {
    return axios.get(routes.professionals()+'?'+ids.map(id => `ids=${encodeURIComponent(id)}`).join('&'));
}

export const getProfessionalsPublicDataByIdAsync = (ids: string[]): Promise<ProfessionalData[]> => {
    return axios.get(routes.professionalsPublicData()+'?'+ids.map(id => `ids=${encodeURIComponent(id)}`).join('&'));
}

export const getProfessionalAvailabilityAsync = (id: string, appointmentType: string, durationMin: number, toDate?: string, fromDate?: string): Promise<Availability[]> => {
    return axios.get(routes.professionalAvailability(id)+getParams({durationMin, appointmentType, from: fromDate, to: toDate}));
}

export const getProfessionalAppointmentsAsync = (id: string, pageSize?: number, offset?: number): Promise<AppointmentData[]> => {
    return axios.get(routes.professionalAppointments(id)+getParams({pageSize, offset}))
}

export const getProfessionalsReducedDataAsync = (): Promise<ProfessionalData[]> => {
    return axios.get(routes.professionals()+getParams({reduced: true}));
}

export const getProfessionalMeAsync = (): Promise<ProfessionalData[]> => {
    return axios.get(routes.professionalMe()).then(p => ([p as any as ProfessionalData]));
}

export const getProfessionalChangeLogAsync = (id: string, pageSize?: number, offset?: number): Promise<ChangeLog[]> => {
    return axios.get(routes.professionalChangeLog(id)+getParams({pageSize, offset}));
}

export const getProfessionalPacksAsync = (id: string, pageSize?: number, offset?: number): Promise<ClientPack[]> => {
    return axios.get(routes.professionalPacks(id)+getParams({pageSize, offset}));
}

export const getProfessionalPackAsync = (professionalId: string, packId: string): Promise<ClientPack> => {
    return axios.get(routes.professionalPack(professionalId, packId));
}

export const updateMeProfessionalAsync = (data: ProfessionalData): Promise<void> => {
    return axios.put(routes.professionalMe(), data);
}

export const deleteProfessionalAsync = (id: string): Promise<void> => {
    return axios.delete(routes.professional(id));
}

export const updateProfessionalAsync = (id: string, data: ProfessionalData): Promise<void> => {
    return axios.put(routes.professional(id), data);
}

export const createProfessionalAsync = (data: ProfessionalData): Promise<string> => {
    return axios.post(routes.professionals(), data);
}

export const getSetsAsync = (type: string): Promise<string[]> => {
    return axios.get(routes.sets(type));
}

export const addValuesToSetAsync = (type: string, valuesToAdd: string[]): Promise<string[]> => {
    return axios.post(routes.setsAdd(type), valuesToAdd);
}

export const updateSetAsync = (type: string, values: string[]): Promise<string[]> => {
    return axios.put(routes.sets(type), values);
}

export const removeValuesFromSetAsync = (type: string, valuesToRemove: string[]): Promise<string[]> => {
    return axios.delete(routes.sets(type)+getParams({ valuesToRemove }));
}

export const getAppointmentsAsync = (from: string, to: string, signal?: GenericAbortSignal, reduced?: boolean): Promise<AppointmentData[]> => {
    return axios.get(routes.appointments()+getParams({from, to, reduced}), {signal})
}

export const getAppointmentAsync = (id: string): Promise<AppointmentData> => {
    return axios.get(routes.appointment(id))
}

export const getMyAppointmentsAsync = (from: string, to: string, reduced?: boolean): Promise<AppointmentData[]> => {
    return axios.get(routes.appointmentsMy()+getParams({from, to, reduced}))
}

export const getAppointmentChangesAsync = (from: string, to: string, pageSize?: number, offset?: number, signal?: GenericAbortSignal): Promise<AppointmentChangeRecordData[]> => {
    return axios.get(routes.appointmentsChanges()+getParams({from, to, pageSize, offset}), {signal})
}

export const getAppointmentChangeLogAsync = (id: string, pageSize?: number, offset?: number): Promise<ChangeLog[]> => {
    return axios.get(routes.appointmentChangeLog(id)+getParams({pageSize, offset}));
}

export const getTasksAsync = (params: GetTasksParams, signal?: GenericAbortSignal): Promise<TaskItemData[]> => {
    return axios.get(routes.tasks() + getNullableParams(params), { signal });
}

export const getTasksPendingSummaryAsync = (signal?: GenericAbortSignal): Promise<TasksPendingSummary> => {
    return axios.get(routes.tasksPendingSummary(), { signal });
}

export const getTaskAssignableUsersAsync = (signal?: GenericAbortSignal): Promise<UserData[]> => {
    return axios.get(routes.tasksAssignableUsers(), { signal });
}

export const getTaskAsync = (id: string, signal?: GenericAbortSignal): Promise<TaskItemData> => {
    return axios.get(routes.task(id), { signal });
}

export const createTaskAsync = (data: CreateTaskItemData, signal?: GenericAbortSignal): Promise<TaskItemData> => {
    return axios.post(routes.tasks(), data, { signal });
}

export const updateTaskAsync = (id: string, data: UpdateTaskItemData, signal?: GenericAbortSignal): Promise<TaskItemData> => {
    return axios.put(routes.task(id), data, { signal });
}

export const updateTaskStatusAsync = (id: string, data: UpdateTaskItemStatusData, signal?: GenericAbortSignal): Promise<TaskItemData> => {
    return axios.put(routes.taskStatus(id), data, { signal });
}

export const assignTaskAsync = (id: string, data: AssignTaskItemData, signal?: GenericAbortSignal): Promise<TaskItemData> => {
    return axios.put(routes.taskAssign(id), data, { signal });
}

export const getTasksByClientAsync = (clientId: string, signal?: GenericAbortSignal): Promise<TaskItemData[]> => {
    return axios.get(routes.tasksByClient(clientId), { signal });
}

export const deleteAppointmentAsync = (id: string, deleteEntireGroup: boolean = false, reason?: AppointmentChangeReason): Promise<void> => {
    const payload: AppointmentChangeReason = {
        reasonOrigin: reason?.reasonOrigin ?? AppointmentChangeOrigin.None,
        reason: reason?.reason ?? '',
        reasonDetails: reason?.reasonDetails,
    };

    return axios.delete(
        routes.appointment(id) + getParams({ deleteEntireGroup }),
        {
            data: payload,
            headers: { 'Content-Type': 'application/json' },
        }
    );
}

export const updateAppointmentAsync = (id: string, data: AppointmentUpdate, ignoreAvailability: boolean = false, updateEntireGroup: boolean = false): Promise<void> => {
    return axios.put(routes.appointment(id) + getParams({ ignoreAvailability, updateEntireGroup }), data);
}

export const updateAppointmentHasHappenedAsync = (id: string, hasHappened: boolean|undefined): Promise<void> => {
    return axios.put(routes.appointmentHasHappened(id), {hasHappened});
}

export const updateAppointmentConfirmationStatusAsync = (id: string, confirmedByClientAt: string | undefined, deniedByClientAt: string | undefined): Promise<void> => {
    return axios.patch(routes.appointmentConfirmationStatus(id), { confirmedByClientAt: confirmedByClientAt ?? null, deniedByClientAt: deniedByClientAt ?? null });
}

export const createAppointmentAsync = async (data: AppointmentData, ignoreAvailability: boolean, showIgnoreAvailabilityWarning: boolean = true): Promise<string> => {
    try {
        return await axios.post(routes.appointments() + `?ignoreAvailability=${ignoreAvailability}`, data)
    } catch(error) {
         if (isConflictError(error) && showIgnoreAvailabilityWarning) {
            const message = (error as any)?.data?.message || translate(TK.noAvailability);
            const proceed = typeof window !== 'undefined'
                ? window.confirm(`${message}\n\n${translate(TK.ignoreAvailability)}?`)
                : false;
            if (proceed) {
                return await axios.post(routes.appointments() + `?ignoreAvailability=${true}`, data)
            }
        }
        throw error;
    }
}

export const createAppointmentsAsync = (data: AppointmentData[], ignoreAvailability: boolean): Promise<BulkAppointmentCreationResult> => {
    return axios.post(routes.appointmentsLot() + `?ignoreAvailability=${ignoreAvailability}`, data);
}

export const confirmClientAppointment = (appointmentId: string): Promise<void> => {
    return axios.post(routes.appointmentConfirm(appointmentId))
}

export const submitSelfScheduleAsync = (data: SelfScheduleRequest): Promise<string> => {
    return axios.post(routes.appointmentsMy(), data)
}

export const getSettingsAsync = (): Promise<SettingsData> => {
    return axios.get(routes.settings());
}

export const getInvoiceCatalogAsync = (): Promise<InvoiceCatalogData> => {
    return axios.get(routes.invoiceCatalog());
}

export const getSubscriptionsAsync = (): Promise<SubscriptionDataResponse> => {
    return axios.get<SubscriptionDataResponse, SubscriptionDataResponse>(routes.subscriptions());
}

export const getMySubscriptionAsync = (): Promise<ClientPack[]> => {
    return axios.get<ClientPack[], ClientPack[]>(routes.mySubscriptions());
}

export const startSubscriptionCheckoutAsync = (id: string): Promise<CheckoutManifest & {testMode?: boolean}> => {
    return axios.post(routes.mySubscriptionCheckout(id));
}

export const upgradeSubscriptionAsync = (data: SubscriptionData): Promise<void> => {
    return axios.put(routes.mySubscriptionsNext(), data);
}

export const getMyTenantClientAsync = (): Promise<ClientData> => {
    return axios.get<ClientData, ClientData>(routes.myTenantClient());
}

export const updateMyTenantClientAsync = (data: ClientData): Promise<void> => {
    return axios.put(routes.myTenantClient(), data);
}

export const updateGeneralSettingsAsync = (settings: GeneralSettingsData): Promise<void> => {
    return axios.put(routes.generalSettings(), settings)
}

export const updateClientsTagsSettingsAsync = (clientTags: GeneralSettingsData['clientTags']): Promise<void> => {
    return axios.put(routes.generalClientsTagsSettings(), clientTags)
}

export const updateExtrasSettingsAsync = (settings: ExtrasSettingsData): Promise<void> => {
    return axios.put(routes.extrasSettings(), settings);
}

export const updateReportsSettingsAsync = (settings: ReportsSettingsData): Promise<void> => {
    return axios.put(routes.reportsSettings(), settings);
}

export const getReportTemplatesAsync = (): Promise<ReportTemplateData[]> => {
    return axios.get(routes.reportTemplates());
}

export const getReportTemplateAsync = (templateId: string, version: number): Promise<ReportTemplateData> => {
    return axios.get(`${routes.reportTemplate(templateId)}?version=${version}`);
}

export const createReportTemplateAsync = (data: ReportTemplateData): Promise<{ id: string }> => {
    return axios.post(routes.reportTemplates(), data);
}

export const updateReportTemplateAsync = (templateId: string, data: ReportTemplateData): Promise<void> => {
    return axios.put(routes.reportTemplate(templateId), data);
}

export const deleteReportTemplateAsync = (templateId: string): Promise<void> => {
    return axios.delete(routes.reportTemplate(templateId));
}

export const generateReportTemplateAsync = (data: GenerateReportTemplateRequest): Promise<ReportSettings> => {
    return axios.post(routes.reportTemplateGenerate(), data);
}

export const getReportTemplateCatalogAsync = (): Promise<CatalogCategoryData[]> => {
    return axios.get(routes.reportTemplateCatalog());
}

export const getPackTemplatesAsync = (): Promise<PackSettings[]> => {
    return axios.get(routes.packTemplates());
}

export const getPackTemplateAsync = (templateId: string, version: number): Promise<PackSettings> => {
    return axios.get(`${routes.packTemplate(templateId)}?version=${version}`);
}

export const createPackTemplateAsync = (data: PackSettings): Promise<{ id: string }> => {
    return axios.post(routes.packTemplates(), data);
}

export const updatePackTemplateAsync = (templateId: string, data: PackSettings, applyToAllVersions?: boolean): Promise<void> => {
    return axios.put(`${routes.packTemplate(templateId)}${getParams({ applyToAllVersions })}`, data);
}

export const deletePackTemplateAsync = (templateId: string): Promise<void> => {
    return axios.delete(routes.packTemplate(templateId));
}

export const updateRemindersSettingsAsync = (settings: RemindersSettingsData): Promise<void> => {
    return axios.put(routes.remindersSettings(), settings);
}

export const updateTaskManagementSettingsAsync = (settings: TaskManagementSettingsData, signal?: GenericAbortSignal): Promise<void> => {
    return axios.put(routes.taskManagementSettings(), settings, { signal });
}

export const updateAlertsSettingsAsync = (settings: AlertsSettingsData): Promise<void> => {
    return axios.put(routes.alertsSettings(), settings);
}

export const testReminderSmsAsync = (data: TestCommunication): Promise<void> => {
    return axios.post(routes.remindersSettingsTestSms(), data);
}

export const testReminderWhatsAppAsync = (data: TestCommunication): Promise<void> => {
    return axios.post(routes.remindersSettingsTestWhatsApp(), data);
}

export const testReminderEmailAsync = (data: TestCommunication): Promise<void> => {
    return axios.post(routes.remindersSettingsTestEmail(), data);
}

export interface TestGoogleReviewData {
    receiverEmailOrPhoneNumber: string;
    smsTemplate?: string;
    emailBodyText?: string;
    branchId?: string;
}

export const testGoogleReviewSmsAsync = (data: TestGoogleReviewData): Promise<void> => {
    return axios.post(routes.googleReviewSettingsTestSms(), data);
}

export const testGoogleReviewEmailAsync = (data: TestGoogleReviewData): Promise<void> => {
    return axios.post(routes.googleReviewSettingsTestEmail(), data);
}

export interface GoogleReviewAnalyticsParams {
    professionalId?: string;
    branchId?: string;
    channel?: CommunicationType;
    from?: string;
    to?: string;
}

export const getGoogleReviewAnalyticsAsync = (params: GoogleReviewAnalyticsParams): Promise<GoogleReviewAnalyticsData> => {
    return axios.get(routes.googleReviewAnalytics() + getParams(params));
}

export const updatePaymentsSettingsAsync = (settings: PaymentsSettingsData): Promise<void> => {
    return axios.put(routes.paymentsSettings(), settings);
}

export const updateClientsLoginSettingsAsync = (settings: ClientsLoginSettingsData): Promise<void> => {
    return axios.put(routes.clientsLoginSettings(), settings);
}

export const updateIntegrationsSettingsAsync = (settings: IntegrationsSettingsData): Promise<void> => {
    return axios.put(routes.integrationsSettings(), settings);
}

export interface GhlUserOption {
    id: string;
    name: string;
    email?: string;
}

export interface GhlAuditLogEntry {
    id: string;
    level: string;
    context: string;
    entityId?: string;
    entityType?: string;
    message: string;
    createdAt: string;
}

export const getGoHighLevelUsersAsync = (): Promise<GhlUserOption[]> => {
    return axios.get(routes.integrationsGoHighLevelUsers());
}

export const getGoHighLevelAuditLogAsync = (pageSize: number, offset: number): Promise<GhlAuditLogEntry[]> => {
    return axios.get(routes.integrationsGoHighLevelAuditLog(pageSize, offset));
}

export const testMailerLiteApiKeyAsync = (request: MailerLiteApiKeyRequest): Promise<void> => {
    return axios.post(routes.integrationsMailerLiteTest(), request);
}

export const getMailerLiteFieldsAsync = (request: MailerLiteApiKeyRequest): Promise<MailerLiteFieldsResponse> => {
    return axios.post(routes.integrationsMailerLiteFields(), request);
}

export const updateInvoicesSettingsAsync = (settings: InvoiceSettingsData): Promise<{ message?: string; Message?: string }> => {
    return axios.put(routes.invoicesSettings(), settings);
}

export const updateConsentManagementSettingsAsync = (settings: ConsentManagementSettingsData): Promise<void> => {
    return axios.put(routes.consentManagementSettings(), settings);
}

export const updateCashRegisterSettingsAsync = (settings: CashRegisterSettings): Promise<void> => {
    return axios.put(routes.cashRegisterSettings(), settings);
}

export const updateWaitingListSettingsAsync = (settings: WaitingListSettingsData): Promise<void> => {
    return axios.put(routes.waitingListSettings(), settings);
}

export const updatePrescriptionsSettingsAsync = (settings: PrescriptionsSettingsData): Promise<void> => {
    return axios.put(routes.prescriptionsSettings(), settings);
}

export const getCashRegisterEntriesAsync = (from?: string, to?: string): Promise<CashRegisterEntryData[]> => {
    return axios.get(routes.cashRegisterEntries()+getParams({from, to}));
}

export interface CashRegisterCountReconciliation {
    countedAmount: number;
    paymentType: string;
    cashRegisterName?: string;
    targetUsername?: string;
}

export const handleCashRegisterPaymentsAsync = (
    paymentIds: string[],
    reconciliation?: CashRegisterCountReconciliation,
): Promise<{ handledCount: number }> => {
    return axios.post(routes.cashRegisterHandle(), { paymentIds, ...reconciliation });
}

export const getPrescriptionsByClientAsync = (clientId: string): Promise<PrescriptionData[]> => {
    return axios.get(routes.prescriptionsByClient(clientId));
}

export const createPrescriptionAsync = (command: object): Promise<PrescriptionData> => {
    return axios.post(routes.prescriptions(), command);
}

export const searchMedicinesAsync = (q: string): Promise<MedicineSearchResultData[]> => {
    return axios.get(routes.medicineSearch(q));
}

export const createCashRegisterReserveAsync = (paymentType: string, reserveAmount: number, description: string, cashRegisterName?: string): Promise<void> => {
    return axios.post(routes.cashRegisterReserve(), { paymentType, reserveAmount, description, cashRegisterName });
}

export const createCashRegisterWithdrawAsync = (
    paymentType: string,
    amount: number,
    description: string,
    cashRegisterName?: string,
    targetUsername?: string,
): Promise<void> => {
    return axios.post(routes.cashRegisterWithdraw(), { paymentType, amount, description, cashRegisterName, targetUsername });
}

export interface CashRegisterReferencePayload {
    cashRegisterName?: string;
    username?: string;
}

export const createCashRegisterTransferAsync = (
    paymentType: string,
    amount: number,
    source: CashRegisterReferencePayload,
    destination: CashRegisterReferencePayload,
    description: string,
): Promise<void> => {
    return axios.post(routes.cashRegisterTransfer(), { paymentType, amount, source, destination, description });
}

export const getNotificationsAsync = (): Promise<Notification[]> => {
    return axios.get(routes.notifications());
}

export const getIncomingMessagesSummaryAsync = (): Promise<IncomingMessagesSummary> => {
    return axios.get(routes.unreadCommunications());
}

export const markCommunicationAsReadAsync = (clientId: string, communicationId: string, read: boolean): Promise<void> => {
    return axios.patch(routes.markCommunicationRead(clientId, communicationId), {read});
}

export const markAllClientCommunicationsAsReadAsync = (clientId: string): Promise<void> => {
    return axios.patch(routes.markAllClientCommunicationsRead(clientId));
}

export const markAllCommunicationsAsReadAsync = (): Promise<void> => {
    return axios.patch(routes.markAllCommunicationsRead());
}

export const sendConfirmationEmailAsync = (): Promise<void> => {
    return axios.post(routes.usersMeConfirmEmail(), {});
}

export const sendConfirmationEmailForUserAsync = (id: string): Promise<void> => {
    return axios.post(routes.usersConfirmEmail(id), {});
}

export const sendResetPasswordEmailAsync = (resetData: ResetPasswordData): Promise<void> => {
    return axios.post(routes.usersResetPassword(), resetData);
}

export const uploadCompanyImageAsync = (file: File): Promise<{ url: string, companyImageFileId: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.patch(routes.companyImageUpload(), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
}


export const createClientPackAsync = (pack: ClientPack): Promise<string> => {
    return axios.post(pack.clientIsProfessional ? routes.professionalPacks(pack.clientId) : routes.clientPacks(pack.clientId), pack);
}

export const updateClientPackAsync = (pack: ClientPack): Promise<void> => {
    return axios.put(pack.clientIsProfessional ? routes.professionalPacks(pack.clientId) : routes.clientPacks(pack.clientId), pack);
}

export const deleteClientPackAsync = (clientId: string, packId: string, clientIsProfessional: boolean): Promise<void> => {
    return axios.delete(clientIsProfessional ? routes.professionalPack(clientId, packId) : routes.clientPack(clientId, packId));
}

export const getClientPackChangeLogAsync = (id: string, pageSize?: number, offset?: number): Promise<ChangeLog[]> => {
    return axios.get(routes.clientPackChangeLog(id)+getParams({pageSize, offset}));
}

export const requestPackMbwayPaymentAsync = (id: string): Promise<{paymentId: string, paymentNotes: string}> => {
    return axios.post(routes.packRequestMBwayPayment(id));
}

export const requestPackMbPaymentAsync = (id: string): Promise<{paymentId: string, paymentNotes: string}> => {
    return axios.post(routes.packRequestEntityRefPayment(id));
}

export const getAdminStatsAsync = (): Promise<AdminStats> => {
    return axios.get(routes.adminStats());
}

export const getAdminEasypayCommissionsAsync = (): Promise<Record<string, { percentage: number; fixed: number }>> => {
    return axios.get(routes.adminEasypayCommissions());
}

export const getAdminCommunicationQuotasAsync = (): Promise<CommunicationQuotas> => {
    return axios.get(routes.adminCommunicationQuotas(), { silentOnForbidden: true } as any);
}

export const getAdminCommunicationsAsync = (from: string, to: string): Promise<CommunicationData[]> => {
    return axios.get(routes.adminCommunications()+getParams({from, to}));
}

export const getAdminPaymentsAsync = (from: string, to: string, branchId?: string): Promise<PaymentData[]> => {
    return axios.get(routes.adminPayments()+getParams({from, to, branchId}));
}

export interface SystemPaymentData {
    tenantId: number;
    id: string;
    date: string;
    value: number;
    type: string;
    origin: PaymentOrigin;
    originId: string;
    clientName: string;
    clientId?: string;
    status: PaymentStatus;
    vat?: number;
    comissionWithoutVat?: number;
    cashRegisterName?: string;
    username?: string;
}

export const getSystemTenantsPaymentsAsync = (from: string, to: string): Promise<SystemPaymentData[]> => {
    return axios.get(routes.systemTenantsPayments()+getParams({from, to}));
}

export interface MinComissionRate {
    percentage: number;
    fixed: number;
}

export const getSystemTenantsPaymentsMinComissionsAsync = (): Promise<Record<string, MinComissionRate>> => {
    return axios.get(routes.systemTenantsPaymentsMinComissions());
}

export const getExpensesAsync = (from: string, to: string, professionalId?: string): Promise<ExpenseData[]> => {
    return axios.get(routes.expenses()+getParams({from, to, professionalId}));
}

export const exportExpensesZipAsync = async (from: string, to: string): Promise<void> => {
    // Note: response interceptor returns response.data directly, so 'blob' is the actual blob
    const blob = await axios.get(routes.expensesExportZip()+getParams({from, to}), {
        responseType: 'blob',
    }) as unknown as Blob;

    if (!blob || blob.size === 0) {
        return;
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fromDate = from.substring(0, 10);
    const toDate = to.substring(0, 10);
    link.setAttribute('download', `expenses-${fromDate}-to-${toDate}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export const getExpenseAsync = (id: string): Promise<ExpenseData> => {
    return axios.get(routes.expense(id));
}

export const getExpenseChangeLogAsync = (id: string, pageSize?: number, offset?: number): Promise<ChangeLog[]> => {
    return axios.get(routes.expenseChangeLog(id)+getParams({pageSize, offset}));
}

const appendExpenseToFormData = (formData: FormData, expense: ExpenseData) => {
    const formatFormNumber = (value: number | string | undefined, fallback: string) => {
        if (value === undefined || value === null) return fallback;

        const numericValue = typeof value === 'number'
            ? value
            : Number(value.toString().replace(',', '.'));

        if (Number.isNaN(numericValue)) return fallback;

        return numericValue.toString().replace(',', '.');
    };

    // Append simple string properties using PascalCase to match C# model
    if (expense.id) formData.append('Id', expense.id);
    formData.append('Name', expense.name || '');
    formData.append('Notes', expense.notes || '');
    formData.append('Type', expense.type || '');
    formData.append('Entity', expense.entity || '');
    if (expense.professionalId) formData.append('ProfessionalId', expense.professionalId);
    if (expense.professionalName) formData.append('ProfessionalName', expense.professionalName);

    // Append payment object using dot notation with PascalCase
    if (expense.payment) {
        formData.append('Payment.Date', expense.payment.date || '');
        formData.append('Payment.Value', formatFormNumber(expense.payment.value, '0'));
        formData.append('Payment.Type', expense.payment.type || '');
        formData.append('Payment.VAT', formatFormNumber(expense.payment.vat, '0'));
        formData.append('Payment.Amount', formatFormNumber(expense.payment.amount, '1'));
    }

    // Append recurring object using dot notation with PascalCase if it exists
    if (expense.recurring) {
        if (expense.recurring.endDate) formData.append('Recurring.EndDate', expense.recurring.endDate);
        if (expense.recurring.unit !== undefined) formData.append('Recurring.Unit', expense.recurring.unit.toString());
        if (expense.recurring.value !== undefined) formData.append('Recurring.Value', expense.recurring.value.toString());
    }

    // Append attachments array using indexed notation with PascalCase
    if (expense.attachments && expense.attachments.length > 0) {
        expense.attachments.forEach((attachment, index) => {
            if (attachment.fileId) formData.append(`Attachments[${index}].FileId`, attachment.fileId);
            if (attachment.fileName) formData.append(`Attachments[${index}].FileName`, attachment.fileName);
            if (attachment.contentType) formData.append(`Attachments[${index}].ContentType`, attachment.contentType);
        });
    }
};

export const createExpenseAsync = (expense: ExpenseData, files?: File[]): Promise<string> => {
    const formData = new FormData();

    // Append the expense data using proper form encoding
    appendExpenseToFormData(formData, expense);

    // Append files if provided
    if (files && files.length > 0) {
        files.forEach(file => {
            formData.append('files', file);
        });
    }

    return axios.post(routes.expenses(), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
}

export const updateExpenseAsync = (id: string, expense: ExpenseData, files?: File[]): Promise<void> => {
    const formData = new FormData();

    // Append the expense data using proper form encoding
    appendExpenseToFormData(formData, expense);

    // Append files if provided
    if (files && files.length > 0) {
        files.forEach(file => {
            formData.append('files', file);
        });
    }

    return axios.put(routes.expense(id), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
}

export const deleteExpenseAsync = (id: string): Promise<void> => {
    return axios.delete(routes.expense(id));
}

export const getStockAsync = (): Promise<StockData[]> => {
    return axios.get(routes.stock());
}

export const getStockChangeLogAsync = (id: string, pageSize?: number, offset?: number): Promise<ChangeLog[]> => {
    return axios.get(routes.stockItemChangeLog(id)+getParams({pageSize, offset}));
}

export const createStockAsync = (stock: StockData): Promise<string> => {
    return axios.post(routes.stock(), stock);
}

export const updateStockAsync = (id: string, stock: StockData): Promise<void> => {
    return axios.put(routes.stockItem(id), stock);
}

export const deleteStockAsync = (id: string): Promise<void> => {
    return axios.delete(routes.stockItem(id));
}

export const buyStockAsync = (stockId: string, expense: ExpenseData): Promise<string> => {
    return axios.post(routes.buyStock(stockId), expense);
}

export const updateStockQuantityAsync = (stockId: string, decreaseStock: UpdateStockQuantity): Promise<void> => {
    return axios.post(routes.stockQuantity(stockId), decreaseStock);
}

export const getStockHistoryAsync = (stockId: string): Promise<StockHistoryData[]> => {
    return axios.get(routes.stockItemHistory(stockId));
}

export const getAdminChangeLogAsync = (from: string, to: string): Promise<ChangeEntityLog[]> => {
    return axios.get(routes.adminChangeLog()+getParams({from, to}));
}

export const postAdminDbCleanupAsync = (): Promise<ChangeEntityLog[]> => {
    return axios.post(routes.adminDbCleanup());
}

export const postPermanentlyDeleteAllDataAsync = (confirmation: string): Promise<ChangeEntityLog[]> => {
    return axios.post(routes.adminPermanentlyDeleteAllData(), {confirmation});
}

export const requestAppointmentMbwayPaymentAsync = (id: string): Promise<{paymentId: string, paymentNotes: string}> => {
    return axios.post(routes.appointmentRequestMBwayPayment(id));
}

export const requestAppointmentMbPaymentAsync = (id: string): Promise<{paymentId: string, paymentNotes: string}> => {
    return axios.post(routes.appointmentRequestEntityRefPayment(id));
}

export const sendAppointmentCheckoutEmailAsync = (id: string): Promise<void> => {
    return axios.post(routes.appointmentSendCheckoutEmail(id));
}

export const sendClientAppointmentsCheckoutEmailAsync = (clientId: string, appointmentIds: string[]): Promise<void> => {
    return axios.post(routes.clientSendAppointmentsCheckoutEmail(clientId), { appointmentIds });
}

export const addPaymentToClientAppointmentsAsync = (clientId: string, appointmentIds: string[], paymentType: string, cashRegisterName: string | undefined): Promise<AppointmentData[]> => {
    return axios.post(routes.clientAddAppointmentsPayment(clientId), { appointmentIds, paymentType, cashRegisterName });
}

export const sendPackCheckoutEmailAsync = (id: string): Promise<void> => {
    return axios.post(routes.packSendCheckoutEmail(id));
}

export const getCompanyPublicDataAsync = (id: string): Promise<CompanyPublicData> => {
    return axios.get(routes.companyPublicData(id));
}

export const postCompanyAppointmentsAsync = (id: string, request: AnonymousScheduleRequest): Promise<string> => {
    return axios.post(routes.companyAppointments(id), request);
}

export const getCampaignsAsync = (): Promise<CampaignReduced[]> => {
    return axios.get(routes.campaigns());
}

export const getCampaignAsync = (id: string): Promise<Campaign> => {
    return axios.get(routes.campaign(id))
}

export const getCampaignProcessesAsync = (id: string): Promise<CampaignProcess[]> => {
    return axios.get(routes.campaignProcesses(id))
}

export const updateCampaignAsync = (id: string, data: CampaignBase): Promise<number> => {
    return axios.put(routes.campaign(id), data);
}

export const createCampaignAsync = (data: CampaignBase): Promise<string> => {
    return axios.post(routes.campaigns(), data) 
}

export const deleteCampaignAsync = (id: string): Promise<void> => {
    return axios.delete(routes.campaign(id))
}

export const testCampaignAsync = (data: CampaignTest): Promise<void> => {
    return axios.post(routes.campaignsTest(), data)
}

export const testCampaignForCampaignAsync = (id: string, to: string): Promise<void> => {
    return axios.post(routes.campaignTest(id), { to });
}

export const estimateCampaignRecipientsAsync = (id: string, config: CampaignBase): Promise<CampaignRecipientEstimate> => {
    return axios.post(routes.campaignEstimateRecipients(id), config);
}

export const sendCampaignAsync = (id: string): Promise<void> => {
    return axios.post(routes.campaignSend(id));
}

export const notifyWhatsAppCampaignInterestAsync = (): Promise<void> => {
    return axios.post(routes.campaignsWhatsAppNotifyMe())
}

export const startCampaignAsync = (id: string): Promise<void> => {
    return axios.post(routes.campaignStart(id))
}

export const duplicateCampaignAsync = (id: string): Promise<string> => {
    return axios.post(routes.campaignDuplicate(id))
}

export const archiveCampaignAsync = (id: string): Promise<void> => {
    return axios.post(routes.campaignArchive(id))
}

export const getCampaignAnalyticsAsync = (id: string): Promise<CampaignAnalytics> => {
    return axios.get(routes.campaignAnalytics(id))
}

export const createCampaignOrderAsync = (campaignId: string, recipientCount: number): Promise<CampaignOrder> => {
    return axios.post(routes.campaignOrders(campaignId), { recipientCount });
}

export const startCampaignOrderCheckoutAsync = (campaignId: string, orderId: string): Promise<CheckoutManifest & { testMode?: boolean }> => {
    return axios.post(routes.campaignOrderCheckout(campaignId, orderId));
}

export const getCampaignOrderAsync = (campaignId: string, orderId: string): Promise<CampaignOrder> => {
    return axios.get(routes.campaignOrder(campaignId, orderId));
}

export const getWhatsAppTemplateCatalogAsync = (): Promise<WhatsAppTemplateCatalogItem[]> => {
    return axios.get(routes.whatsAppTemplateCatalog())
}

export const getCampaignWhatsAppTemplateCatalogAsync = (): Promise<WhatsAppTemplateCatalogItem[]> => {
    return axios.get(routes.whatsAppTemplateCatalogCampaign())
}

export const syncWhatsAppTemplateCatalogAsync = (): Promise<void> => {
    return axios.post(routes.whatsAppTemplateCatalogSync())
}

export const createWhatsAppTemplateAsync = (data: CreateWhatsAppTemplateRequest): Promise<WhatsAppMessageTemplate> => {
    return axios.post(routes.whatsAppTemplates(), data).then(r => r.data)
}

export const updateWhatsAppTemplateAsync = (id: string, data: UpdateWhatsAppTemplateRequest): Promise<void> => {
    return axios.put(routes.whatsAppTemplate(id), data)
}

export const deleteWhatsAppTemplateAsync = (id: string, name: string): Promise<void> => {
    return axios.delete(routes.whatsAppTemplate(id), { params: { name } })
}

export const createInvoiceForAppointmentAsync = (appointmentId: string, invoice: InvoiceData): Promise<Invoice> => {
    return axios.post(routes.appointmentInvoices(appointmentId), invoice)
}

export const createInvoiceForAppointmentsAsync = (invoice: InvoiceManyAppointmentsData): Promise<Invoice> => {
    return axios.post(routes.appointmentsInvoices(), invoice)
}

export const createInvoiceForPackAsync = (packId: string, invoice: InvoiceData): Promise<Invoice> => {
    return axios.post(routes.packInvoices(packId), invoice)
}

export const createStandaloneInvoiceAsync = (invoice: InvoiceData): Promise<Invoice> => {
    return axios.post(routes.invoices(), invoice)
}

export const getInvoicesAsync = (clientId: string, branchId?: string): Promise<InvoiceReduced[]> => {
    return axios.get(routes.invoices() + getNullableParams({ clientId, branchId }))
}

export const getInvoiceAsync = (invoiceId: string): Promise<Invoice> => {
    return axios.get(routes.invoice(invoiceId))
}

export const assignInvoiceToEntitiesAsync = (invoiceId: string, data: AssignInvoiceData): Promise<void> => {
    return axios.post(routes.invoiceAssign(invoiceId), data)
}

export interface AdminInvoicesQuery {
    status?: InvoiceState;
    type?: InvoiceDocumentType;
    page?: number;
    pageSize?: number;
    clientId?: string;
    branchId?: string;
    withoutReceipt?: boolean;
}

export const getAdminInvoicesAsync = (from: string, to: string, query?: AdminInvoicesQuery): Promise<InvoiceReduced[]> => {
    const { status, type, page, pageSize, clientId, branchId, withoutReceipt } = query ?? {};
    const statusParam = typeof status === 'number' ? InvoiceState[status] : undefined;
    const typeParam = typeof type === 'number' ? InvoiceDocumentType[type] : undefined;
    return axios.get(routes.invoices() + getNullableParams({ from, to, status: statusParam, type: typeParam, clientId, branchId, page, pageSize, withoutReceipt: withoutReceipt || undefined }))
}

export const getAdminInvoicesSummaryAsync = (from: string, to: string, query?: AdminInvoicesQuery): Promise<InvoiceSummaryData> => {
    const { status, type, clientId, branchId } = query ?? {};
    const statusParam = typeof status === 'number' ? InvoiceState[status] : undefined;
    const typeParam = typeof type === 'number' ? InvoiceDocumentType[type] : undefined;
    return axios.get(routes.invoicesSummary() + getNullableParams({ from, to, status: statusParam, type: typeParam, clientId, branchId }))
}

export const generateInvoicePdfAsync = (invoiceId: string, isTenantInvoice = false): Promise<{url: string}> => {
    return axios.get(routes.invoice(invoiceId)+'/pdf' + (isTenantInvoice ? '?isTenantInvoice=true' : ''))
}

export const downloadInvoicesSaftFileAsync = (year: string, month: string): Promise<{url: string}|{message: string}> => {
    return axios.get(routes.invoicesSaft(year, month))
}

export const exportInvoicePdfsZipAsync = async (from: string, to: string, query?: AdminInvoicesQuery): Promise<void> => {
    const { status, type, clientId, branchId } = query ?? {};
    const statusParam = typeof status === 'number' ? InvoiceState[status] : undefined;
    const typeParam = typeof type === 'number' ? InvoiceDocumentType[type] : undefined;
    const blob = await axios.get(routes.invoicesExportPdfs() + getNullableParams({
        from,
        to,
        status: statusParam,
        type: typeParam,
        clientId,
        branchId,
    }), {
        responseType: 'blob',
    }) as unknown as Blob;

    if (!blob || blob.size === 0) {
        return;
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoices-${from.substring(0, 10)}-to-${to.substring(0, 10)}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export const sendInvoiceByEmailAsync = (invoiceId: string, data: InvoiceEmailSending): Promise<void> => {
    return axios.post(routes.invoice(invoiceId)+'/send-email', data)
}

export const updateInvoiceStateAsync = (invoiceId: string, data: ChangeInvoiceState): Promise<Invoice> => {
    return axios.put(routes.invoiceState(invoiceId), data)
}

export const issueCreditNoteAsync = (invoiceId: string, data: CreditNoteData): Promise<Invoice> => {
    return axios.patch(routes.invoiceCreditNote(invoiceId), data)
}

export const emitInvoiceReceiptAsync = (invoiceId: string, data: MarkInvoicePaidData): Promise<Invoice> => {
    return axios.patch(routes.invoiceReceipt(invoiceId), data)
}

export const getTaxExemptionsAsync = (): Promise<TaxExemption[]> => {
    return axios.get(routes.taxExemptions())
}

export const getTaxRatesAsync = (): Promise<TaxRate[]> => {
    return axios.get(routes.taxRates())
}

const getBrowserInfo = () => {
    var userAgent = navigator.userAgent;
    var browserName = navigator.appName;
    var browserVersion = navigator.appVersion;
    var os = navigator.platform;
    var screenResolution = window.screen.width + 'x' + window.screen.height;
    var viewportSize = window.innerWidth + 'x' + window.innerHeight;
    var deviceType = /Mobi|Android/i.test(userAgent) ? 'Mobile' : 'Desktop';

    return {
        userAgent: userAgent,
        browserName: browserName,
        browserVersion: browserVersion,
        os: os,
        screenResolution: screenResolution,
        viewportSize: viewportSize,
        deviceType: deviceType
    }
}

export const monitorError = (error: unknown, errorInfo: React.ErrorInfo, state: ApplicationState): Promise<void> => {
    const log = JSON.stringify({
        exception: error instanceof Error ? `${error.message} - ${error.stack ?? ''}`
            : typeof error === 'string' ? error
            : error,
        location: window.location.href,
        componentStack: errorInfo?.componentStack,
        browser: getBrowserInfo(),
        user: getMyUser(state),
    })
         
    return axios.post(routes.monitoringError(), {error: log})
}

export const sendChatMessage = (dialog: DialogData): Promise<{answer: string, usage: {inputTokens: number, outputTokens: number}}> => {
    return axios.post(routes.chatDialog(), dialog)
}

export const syncIntegrationAsync = (type: IntegrationType, createIfNotExists: boolean = false): Promise<IntegrationSyncResult> => {
    return axios.post(routes.integrationsSync(), { type, createIfNotExists }, { timeout: 300000 }) // 5 minutes timeout
}

export const syncGoHighLevelCalendarsAsync = (): Promise<IntegrationSyncResult> => {
    return axios.post(routes.integrationsGoHighLevelSyncCalendars(), null, { timeout: 300000 })
}

export const clearIntegrationAsync = (type: IntegrationType): Promise<IntegrationSyncResult> => {
    return axios.post(routes.integrationsClear(), { type }, { timeout: 600000 }) // 10 minutes timeout
}

export const getWaitingListByProfessionalAsync = (professionalId: string): Promise<WaitingListItem[]> => {
    return axios.get(routes.waitingListByProfessional(professionalId));
}

export const getWaitingListByClientAsync = (clientId: string): Promise<WaitingListItem[]> => {
    return axios.get(routes.waitingListByClient(clientId));
}

export const getWaitingListAsync = (serviceId?: string, professionalId?: string): Promise<WaitingListItem[]> => {
    return axios.get(routes.waitingList() + getParams({ serviceId, professionalId }));
}

export const addToWaitingListAsync = (item: WaitingListItem): Promise<void> => {
    return axios.post(routes.waitingList(), item);
}

export const removeFromWaitingListAsync = (clientId: string, service: string, professionalId?: string): Promise<void> => {
    return axios.delete(routes.waitingList() + getParams({ clientId, service, professionalId }));
}

export const getWaitingListHistoryAsync = (serviceId?: string, professionalId?: string, from?: string, to?: string): Promise<WaitingListHistoryData[]> => {
    return axios.get(routes.waitingListHistory() + getParams({ serviceId, professionalId, from, to }));
}

export const getSurveysAsync = (): Promise<SurveySettingsData[]> => {
    return axios.get(routes.surveys());
}

export const getSurveyPreviewAsync = (id: string): Promise<SurveyTokenData> => {
    return axios.get(routes.surveyPreview(id));
}

export const getSurveyPreviewLinkAsync = (id: string): Promise<string> => {
    return axios.get(routes.surveyPreviewLink(id));
}

export const createSurveyAsync = (data: SurveyDraftData): Promise<string> => {
    return axios.post(routes.surveys(), data);
}

export const generateSurveyAsync = (data: GenerateSurveyRequest): Promise<SurveyDraftData> => {
    return axios.post(routes.surveyGenerate(), data);
}

export const updateSurveyAsync = (id: string, data: SurveyUpdateRequest): Promise<void> => {
    return axios.put(routes.survey(id), data);
}

export const deleteSurveyAsync = (id: string): Promise<void> => {
    return axios.delete(routes.survey(id));
}

export const sendSurveyAsync = (surveyId: string, clientId: string, appointmentId?: string): Promise<void> => {
    return axios.post(routes.surveySend(surveyId), null, { params: { clientId, appointmentId } });
}

export const getSurveyShareLinkAsync = (surveyId: string): Promise<string> => {
    return axios.get(routes.surveyShareLink(surveyId));
}

export const sendSurveyToContactAsync = (surveyId: string, data: SendSurveyToContactRequest): Promise<void> => {
    return axios.post(routes.surveySendToContact(surveyId), data);
}

export const getSurveyAnalyticsAsync = (params: {[name: string]: any}): Promise<SurveyAnalyticsData> => {
    return axios.get(routes.surveyAnalytics() + getParams(params));
}

export const getSurveyVerifyAsync = (token: string): Promise<SurveyTokenData> => {
    return axios.get(routes.surveyVerify(), { headers: { Authorization: `Bearer ${token}` } });
}

export const postSurveySubmitAsync = (
    token: string,
    values: Record<string, string>,
    submitAnonymously: boolean,
    contact?: { contactName?: string; contactEmail?: string; contactPhone?: string },
): Promise<void> => {
    return axios.post(routes.surveySubmit(), { values, submitAnonymously, ...contact }, { headers: { Authorization: `Bearer ${token}` } });
}

export const getGoogleCalendarStatusAsync = (): Promise<{ isConnected: boolean }> =>
    axios.get(routes.googleCalendarStatus());

export const getGoogleCalendarAuthUrlAsync = (redirectUri: string): Promise<{ authUrl: string }> =>
    axios.get(routes.googleCalendarAuthUrl(), { params: { redirectUri } });

export const connectGoogleCalendarAsync = (code: string, redirectUri: string): Promise<void> =>
    axios.post(routes.googleCalendarConnect(), { code, redirectUri });

export const disconnectGoogleCalendarAsync = (): Promise<void> =>
    axios.delete(routes.googleCalendarDisconnect());

export const startAiScriberSessionAsync = (
    appointmentId: string,
    reportTemplateId: string,
    language: string,
): Promise<{ sessionId: string }> =>
    axios.post(routes.aiScriberSession(appointmentId), { reportTemplateId, language });

export const postAiScriberAudioChunkAsync = (appointmentId: string, sessionId: string, chunk: Blob): Promise<void> =>
    axios.post(routes.aiScriberSessionAudioChunk(appointmentId, sessionId), chunk, {
        headers: { 'Content-Type': 'audio/webm' },
    });

export const stopAiScriberSessionAsync = (appointmentId: string, sessionId: string): Promise<void> =>
    axios.post(routes.aiScriberSessionStop(appointmentId, sessionId));
